import { useEffect, useState } from "react";
import { List, Stack, StackProps } from "@mui/material";
import {
  Home,
  CloudDownload,
  Search,
  Storage,
  Bolt,
  MenuBook,
  Description,
} from "@mui/icons-material";
import { DrawerNavMenuItem } from "./DrawerNavMenuItem";
import { getMe, MeResponse } from "../../api/me";

interface DrawerNavMenuProps extends Omit<StackProps, "children"> {
  collapsed?: boolean;
}

// Flat top-level nav, matching the Insight template. Sub-features
// (e.g. "New export") live inside their section's page, not the drawer.
//
//   Home              /
//   Exports           /exports        (export workflows only — submit + history)
//   Search envs       /search-envs    (ephemeral OpenSearch clusters per export)
//   Cluster activity  /activity       (live view of every workflow regardless of kind)
//   Schema reference  /schema         (field-level schema of paim search-env documents)
//   Index management  /admin/archive  (admin-only — operator-side ops)
//
// "Index management" is gated to role=admin so non-admin users don't
// see a section they can't act on. The /api/me call is per-render of
// the drawer, which is fine for now; if it becomes hot we can lift it
// into a context cache.

export function DrawerNavMenu({ collapsed = false, ...props }: DrawerNavMenuProps) {
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    getMe()
      .then(setMe)
      .catch(() => undefined);
  }, []);

  const isAdmin = me?.role === "admin";

  return (
    <Stack justifyContent="space-between" flexGrow={1} width="100%" {...props}>
      <List disablePadding>
        <DrawerNavMenuItem
          icon={<Home />}
          label="Home"
          path="/"
          exact
          collapsed={collapsed}
        />
        <DrawerNavMenuItem
          icon={<CloudDownload />}
          label="Datasets"
          path="/exports"
          collapsed={collapsed}
        />
        <DrawerNavMenuItem
          icon={<Search />}
          label="Search envs"
          path="/search-envs"
          collapsed={collapsed}
        />
        <DrawerNavMenuItem
          icon={<Bolt />}
          label="Cluster activity"
          path="/activity"
          collapsed={collapsed}
        />
        <DrawerNavMenuItem
          icon={<MenuBook />}
          label="Schema reference"
          path="/schema"
          collapsed={collapsed}
        />
        <DrawerNavMenuItem
          icon={<Storage />}
          label="Index management"
          // Operator-side ops — admin-only. Shown to everyone but grayed
          // (non-navigable) for non-admins so they know the section exists.
          // Lands on the inventory page; the active-highlight also picks up
          // sibling /admin/* routes via the prefix match.
          path="/admin/archive"
          collapsed={collapsed}
          disabled={!isAdmin}
          tooltip={isAdmin ? undefined : "Admin only"}
        />
        <DrawerNavMenuItem
          icon={<Description />}
          label="Archive description"
          // Read-only for everyone (the describe *action* inside is admin-
          // gated). Distinct prefix from /admin/archive so the two admin
          // items don't both highlight (the active match is a startsWith).
          path="/admin/description"
          collapsed={collapsed}
        />
      </List>
    </Stack>
  );
}
